/**
 * Platform dashboard — Zod schemas.
 *
 * A single `/overview` endpoint aggregates the cross-tenant
 * numbers and alerts that the dashboard needs. We deliberately
 * avoid making the dashboard compose its own data from five
 * different queries on the client — the backend already knows how
 * to roll up efficiently, and a single cacheable response is
 * cheaper than five independently-staled fragments.
 *
 * Alert + trend shapes live in `components/dashboards/schemas.ts`
 * so the per-tenant overview can share identical types and the
 * same panel components.
 */

import { z } from "zod";
import {
  dashboardAlertSchema,
  signInTrendPointSchema,
} from "@/components/dashboards/schemas";

// Re-export shared types so existing imports from this file
// continue to resolve (DashboardAlert etc were historically
// defined here).
export type {
  DashboardAlert,
  DashboardAlertKind,
  DashboardAlertSeverity,
} from "@/components/dashboards/schemas";
export {
  dashboardAlertSchema,
  dashboardAlertKindSchema,
  dashboardAlertSeveritySchema,
} from "@/components/dashboards/schemas";

export const platformKpisSchema = z.object({
  tenants_total: z.number().int().nonnegative(),
  tenants_active: z.number().int().nonnegative(),
  users_total: z.number().int().nonnegative(),
  passkeys_total: z.number().int().nonnegative(),
  passkey_coverage_percent: z.number().int().min(0).max(100),
  sign_ins_24h: z.number().int().nonnegative(),
  failed_sign_ins_24h: z.number().int().nonnegative(),
  step_up_challenges_24h: z.number().int().nonnegative(),

  // Scope-split admin / operator headcount. All counts are of
  // DISTINCT admin_user rows — an admin delegated into three
  // orgs across two tenants is counted once in org_admins_active.
  // Backend source: report.PlatformKPIs in
  // internal/ops/report/platform_overview.go.
  platform_admins_active: z.number().int().nonnegative(),
  platform_operators_active: z.number().int().nonnegative(),
  tenant_admins_active: z.number().int().nonnegative(),
  tenant_operators_active: z.number().int().nonnegative(),
  org_admins_active: z.number().int().nonnegative(),
  org_operators_active: z.number().int().nonnegative(),
});
export type PlatformKpis = z.infer<typeof platformKpisSchema>;

export const platformOverviewSchema = z.object({
  kpis: platformKpisSchema,
  alerts: z.array(dashboardAlertSchema),
  /** Last 14 days of sign-ins (daily). */
  signin_trend: z.array(signInTrendPointSchema),
  /** Most recently created tenants. */
  recent_tenants: z.array(
    z.object({
      id: z.string(),
      slug: z.string(),
      name: z.string(),
      created_at: z.string(),
      user_count: z.number().int().nonnegative(),
    }),
  ),
});
export type PlatformOverview = z.infer<typeof platformOverviewSchema>;
